import { ShoppingOutlined } from '@ant-design/icons';
import { Button, Select } from 'antd';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateProps } from '../../../contextRedux/Product/ProductModel';
import { SearchInput } from '../../../customComponents/input/SearchInput';
import { CustomTable } from '../../../customComponents/table/CustomTable';
import { RootState } from '../../../store';
import { ProductColumn } from './ProductColumn';

const ProductTable: React.FC = () => {
  const dispatch = useDispatch();
  const [selectedCategory, setSelectedCategory] = useState<any>(null);

  const reduxProduct = useSelector((state: RootState) => state.models?.product);
  const products = useSelector((state: RootState) => state.store?.product.data ?? []);
  const systemInformations = useSelector((state: RootState) => state.store?.systemInformation.data ?? []);

  const pageSizes = [10, 20, 30, 50];
  const currentPage = Math.floor(reduxProduct.props.skip / reduxProduct.props.take) + 1;

  useEffect(() => {
    if (reduxProduct.props.categoryId) {
      setSelectedCategory(reduxProduct.props.categoryId);
    }
  }, [reduxProduct.props.categoryId]);

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      dispatch(updateProps({ field: 'searchTerm', value }));
      dispatch(updateProps({ field: 'skip', value: 0 }));
    }, 500),
    [dispatch]
  );

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    debouncedSearch(e.target.value);
  };

  const handleCategoryChange = (value: any) => {
    setSelectedCategory(value);
    dispatch(updateProps({ field: 'categoryId', value }));
    dispatch(updateProps({ field: 'skip', value: 0 }));
  };

  const paginationConfig = {
    current: currentPage,
    pageSize: reduxProduct.props.take,
    total: reduxProduct.props.totalItems,
    onChange: (page: number, pageSize: number) => {
      const skipValue = (page - 1) * pageSize;
      dispatch(updateProps({ field: 'skip', value: skipValue }));
      dispatch(updateProps({ field: 'take', value: pageSize }));
    },
    showSizeChanger: true,
    pageSizeOptions: pageSizes.map((size) => size.toString()),
    onShowSizeChange: (_current: number, size: number) => {
      dispatch(updateProps({ field: 'skip', value: 0 }));
      dispatch(updateProps({ field: 'take', value: size }));
    },
    showQuickJumper: false,
  };

  const renderTextWithHover = (text: string) => {
    const maxLength = 70;

    // Function to strip HTML tags and normalize whitespace
    const stripHtml = (html: string) => {
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      // Get text content and replace multiple spaces/newlines with a single space
      return (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim();
    };

    if (typeof text === 'string') {
      // Strip HTML tags and normalize whitespace
      const cleanText = stripHtml(text);

      if (cleanText.length > maxLength) {
        return (
          <span title={cleanText} style={{ whiteSpace: 'nowrap' }}>{`${cleanText.substring(0, maxLength)}...`}</span>
        );
      }
      return <span style={{ whiteSpace: 'nowrap' }}>{cleanText}</span>;
    }
    return text;
  };

  const customColumns = ProductColumn().map((col: any) => ({
    ...col,
    ellipsis: true,
    render: (text: any, record: any) => {
      if (col.render) {
        return col.render(record);
      } else if (col.dataIndex === 'name' || col.dataIndex === 'description') {
        return renderTextWithHover(text);
      } else if (col.dataIndex === 'isInStock') {
        return text == true ? 'DA' : 'NU';
      } else if (col.dataIndex === 'url') {
        return text != null ? (
          <Button
            icon={<ShoppingOutlined />}
            onClick={() => window.open(text, '_blank')} // `text` holds the actual URL
          />
        ) : (
          <span>-</span>
        );
      }
      return text;
    },
    width: 200,
  }));

  return (
    <div>
      <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
        <SearchInput
          placeholder='Search...'
          defaultValue={reduxProduct.props.searchTerm}
          onChange={handleSearch}
          style={{ flex: 1 }}
        />
        <Select
          placeholder='Select Category'
          allowClear
          value={selectedCategory}
          onChange={handleCategoryChange}
          showSearch
          optionFilterProp='children'
          style={{ width: '400px' }}
        >
          {systemInformations.map((category: any) => (
            <Select.Option key={category.id} value={category.category_Id}>
              {category.name}
            </Select.Option>
          ))}
        </Select>
      </div>
      <CustomTable
        initialDataSource={products ?? []}
        pagination={paginationConfig}
        columns={customColumns}
        rowKey='id'
      />
    </div>
  );
};

export default ProductTable;
